import { ALTERSVERGLEICH, OPT_VERMOEGEN, SHOW_ALTERSVERGLEICH_WERTE, VermoegensPosten } from "../state/schema";
import {
  card, h, jaNein, moneyInput, note, objText, objTextarea, selectInput, textInput, textareaInput,
} from "../components/ui";
import { uploadBlock } from "../components/upload";
import {
  berechneHaushalt, gehaltszahlungenProJahr, summeSparbeitraege, summeVersicherungsbeitraege,
} from "../logic/calc";
import { ageFromISO, amountToInput, formatEUR, parseAmount } from "../logic/format";
import {
  vAusgaben, vEinkommen, vGas, vKrankenversicherung, vStrom, vVermoegen,
} from "../logic/validation";
import { Step } from "./types";

const newId = () => "p-" + Math.random().toString(36).slice(2, 10);

/* ---------- 9 · Patrimonio y reservas ---------- */
export const stepVermoegen: Step = {
  id: "vermoegen",
  section: "vermoegen",
  eyebrow: "Patrimonio",
  title: "Esto es lo que has construido hasta ahora",
  subtitle: "Un resumen de tus bienes y reservas.",
  validate: vVermoegen,
  build(ctx) {
    const s = ctx.store;
    const p = () => s.persist();
    const chips = h("div", { class: "chips" });
    const cardsWrap = h("div");

    const ensure = () => {
      const sel: string[] = s.state.vermoegen.posten.map((x) => x.kategorie);
      return sel;
    };

    const toggle = (kat: string) => {
      s.update((st) => {
        const i = st.vermoegen.posten.findIndex((x) => x.kategorie === kat);
        if (i >= 0) st.vermoegen.posten.splice(i, 1);
        else st.vermoegen.posten.push(emptyPosten(kat));
      }, false);
      renderChips();
      renderCards();
    };

    const renderChips = () => {
      chips.innerHTML = "";
      const sel = ensure();
      OPT_VERMOEGEN.forEach((kat) => {
        const active = sel.includes(kat);
        chips.appendChild(h("button", { type: "button", class: "chip", text: kat, attrs: { "aria-pressed": String(active) }, on: { click: () => toggle(kat) } }));
      });
    };

    const renderCards = () => {
      cardsWrap.innerHTML = "";
      s.state.vermoegen.posten.forEach((v) => cardsWrap.appendChild(renderPostenCard(v, p)));
    };

    const detail = h("div");
    const renderDetail = () => {
      detail.innerHTML = "";
      if (s.get("vermoegen.vorhanden") !== "ja") return;
      detail.append(
        card(h("div", { class: "card__title", text: "¿Qué categorías tienes?" }),
          h("p", { class: "card__hint", text: "Elige todo lo que se aplique a ti." }), chips),
        cardsWrap,
        altersvergleichCard(s)
      );
      renderChips();
      renderCards();
    };
    renderDetail();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "¿Ya has acumulado bienes o patrimonio? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "vermoegen.vorhanden", renderDetail, true)),
      detail);
  },
};

function emptyPosten(kategorie: string): VermoegensPosten {
  return { id: newId(), kategorie, wert: "", wertUnbekannt: false, sparbeitrag: "", anbieter: "", notiz: "" };
}

function renderPostenCard(v: VermoegensPosten, p: () => void): HTMLElement {
  const body = h("div");
  const draw = () => {
    body.innerHTML = "";
    const id = "wt-" + v.id;
    const toggle = h("div", { class: "consent", style: { padding: "4px 0" } },
      h("input", { id, type: "checkbox", checked: v.wertUnbekannt,
        on: { change: (e: Event) => { v.wertUnbekannt = (e.target as HTMLInputElement).checked; p(); draw(); } } }),
      h("label", { for: id, text: "No conozco el valor" }));
    body.append(
      objText(v, "wert", p, { label: "Valor actual aproximado", money: true, disabled: v.wertUnbekannt }),
      toggle,
      h("div", { class: "grid-2" },
        objText(v, "sparbeitrag", p, { label: "Aportación de ahorro mensual", money: true, optional: true }),
        objText(v, "anbieter", p, { label: "Proveedor", optional: true })),
      objTextarea(v, "notiz", p, { label: "Nota", optional: true, rows: 2 })
    );
  };
  draw();
  return h("div", { class: "subcard" }, h("div", { class: "subcard__title", style: { marginBottom: "10px" }, text: v.kategorie }), body);
}

function altersvergleichCard(s: any): HTMLElement {
  // Hasta la verificación de la fuente (brief §14) no se publican cifras concretas.
  if (!SHOW_ALTERSVERGLEICH_WERTE) {
    return card(
      h("div", { class: "card__title", text: "Valor de referencia de tu grupo de edad" }),
      note("info", "Añadiremos un valor de referencia fiable para tu grupo de edad en cuanto verifiquemos la base de datos. Lo decisivo es, de todos modos, que ahorres con regularidad para tus objetivos: tu asesor evaluará tu situación de forma personal.")
    );
  }
  const age = ageFromISO(s.get("stammdaten.geburtsdatum"));
  const rows = ALTERSVERGLEICH.map((r) => {
    const [from, to] = r.range.split(" a ").map((x) => parseInt(x, 10));
    const active = age != null && age >= from && age <= to;
    return h("div", {
      class: "kv__row",
      style: active ? { background: "rgba(55,196,241,0.12)", borderRadius: "8px", padding: "8px 10px" } : {},
    },
      h("span", { class: "kv__k", style: { color: "var(--on-card-muted)" }, text: r.range + " años" }),
      h("span", { class: "kv__v", style: { color: "var(--on-card)" }, text: formatEUR(r.wert) }));
  });
  return card(
    h("div", { class: "card__title", text: "Valor de referencia de tu grupo de edad" }),
    h("div", { class: "kv" }, ...rows),
    h("p", { class: "field__optional", text: "Fuente: Bundeszentrale für politische Bildung (Alemania)." }),
    note("info", "Este valor es solo una orientación estadística, no un objetivo personal.")
  );
}

/* ---------- 10 · Electricidad ---------- */
export const stepStrom: Step = {
  id: "strom",
  section: "haushalt",
  eyebrow: "Chequeo del hogar",
  title: "Electricidad",
  subtitle: "Tu suministro de electricidad actual.",
  validate: vStrom,
  build(ctx) {
    const s = ctx.store;
    const detail = h("div");
    const render = () => {
      detail.innerHTML = "";
      if (s.get("strom.eigenerVertrag") !== "ja") return;
      detail.appendChild(card(
        h("div", { class: "grid-2" },
          textInput(s, "strom.anbieter", { label: "Compañía de electricidad", required: true }),
          moneyInput(s, "strom.abschlag", { label: "Pago mensual a cuenta", required: true })),
        h("div", { class: "grid-2" },
          textInput(s, "strom.vertragsende", { label: "Fin del contrato", optional: true }),
          textInput(s, "strom.kuendigungsfrist", { label: "Plazo de cancelación", optional: true })),
        h("div", { class: "field" },
          h("div", { class: "field__label", html: "Última factura anual <span style='color:var(--neg)'>*</span>" }),
          h("p", { class: "field__optional", text: "Sube tu última factura de electricidad: es necesaria para el análisis." }),
          uploadBlock(s, { kategorie: "Stromabrechnung", onChange: () => ctx.store.emit() }))
      ));
    };
    render();
    return h("div", {},
      card(h("div", { class: "card__title", html: "¿Recibes electricidad actualmente con un contrato propio? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "strom.eigenerVertrag", render, true)),
      detail);
  },
};

/* ---------- 11 · Gas ---------- */
export const stepGas: Step = {
  id: "gas",
  section: "haushalt",
  eyebrow: "Chequeo del hogar",
  title: "Gas",
  subtitle: "Si calientas o cocinas con gas.",
  validate: vGas,
  build(ctx) {
    const s = ctx.store;
    const detail = h("div");
    const render = () => {
      detail.innerHTML = "";
      if (s.get("gas.eigenerVertrag") !== "ja") return;
      detail.appendChild(card(
        h("div", { class: "grid-2" },
          textInput(s, "gas.anbieter", { label: "Compañía de gas", required: true }),
          moneyInput(s, "gas.abschlag", { label: "Pago mensual a cuenta", required: true })),
        h("div", { class: "grid-2" },
          textInput(s, "gas.vertragsende", { label: "Fin del contrato", optional: true }),
          textInput(s, "gas.kuendigungsfrist", { label: "Plazo de cancelación", optional: true })),
        h("div", { class: "field" },
          h("div", { class: "field__label", html: "Última factura anual <span style='color:var(--neg)'>*</span>" }),
          h("p", { class: "field__optional", text: "Sube tu última factura de gas: es necesaria para el análisis." }),
          uploadBlock(s, { kategorie: "Gasabrechnung", onChange: () => ctx.store.emit() }))
      ));
    };
    render();
    return h("div", {},
      card(h("div", { class: "card__title", html: "¿Recibes gas actualmente con un contrato propio? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "gas.eigenerVertrag", render, true),
        note("info", 'Si tienes calefacción urbana, calefacción central o los gastos de calefacción están incluidos en el alquiler, puedes elegir «No».')),
      detail);
  },
};

/* ---------- 11b · Seguro médico público ---------- */
export const stepKrankenversicherung: Step = {
  id: "krankenversicherung",
  section: "haushalt",
  eyebrow: "Chequeo del hogar",
  title: "Seguro médico",
  subtitle: "Tu seguro médico público.",
  validate: vKrankenversicherung,
  build(ctx) {
    const s = ctx.store;
    const detail = h("div");

    const bonusDetail = h("div");
    const renderBonus = () => {
      bonusDetail.innerHTML = "";
      if (s.get("krankenversicherung.bonusprogramme") === "ja")
        bonusDetail.appendChild(h("div", { style: { marginTop: "10px" } },
          textareaInput(s, "krankenversicherung.bonusprogrammeText", {
            label: "¿Qué programas de bonificación usas?", optional: true, rows: 2,
            placeholder: "p. ej. bono de prevención, deporte, cursos de salud …",
          })));
    };

    const render = () => {
      detail.innerHTML = "";
      if (s.get("krankenversicherung.gesetzlichVersichert") !== "ja") return;
      detail.append(
        card(
          h("div", { class: "card__title", text: "Tu seguro médico" }),
          textInput(s, "krankenversicherung.anbieter", { label: "Nombre de tu seguro médico (Krankenkasse)", required: true, placeholder: "p. ej. TK, AOK, Barmer, DAK …" })
        ),
        card(
          h("div", { class: "card__title", text: "Programas de bonificación" }),
          h("p", { class: "card__hint", text: "Muchos seguros médicos premian, p. ej., la prevención o el deporte con programas de bonificación." }),
          jaNein(s, "krankenversicherung.bonusprogramme", renderBonus),
          bonusDetail
        ),
        card(
          h("div", { class: "card__title", html: "¿Qué te mantiene actualmente en tu seguro médico? <span style='color:var(--neg)'>*</span>" }),
          textareaInput(s, "krankenversicherung.halt", {
            label: "Tu respuesta", required: true, rows: 2,
            placeholder: "p. ej. buen servicio, prestaciones adicionales, costumbre, nada en concreto …",
          })
        )
      );
      renderBonus();
    };
    render();

    return h("div", {},
      card(
        h("div", { class: "card__title", html: "¿Tienes seguro médico público (gesetzlich)? <span style='color:var(--neg)'>*</span>" }),
        jaNein(s, "krankenversicherung.gesetzlichVersichert", render),
        note("info", 'Las personas con seguro privado o aseguradas por familia pueden elegir «No».')),
      detail);
  },
};

/* ---------- 12 · Ingresos + comprobante ---------- */
export const stepEinkommen: Step = {
  id: "einkommen",
  section: "haushalt",
  eyebrow: "Chequeo del hogar",
  title: "Ingresos",
  subtitle: "Indica todos los importes como valores mensuales.",
  leaveMessage: "Ahora veamos tu punto de partida.",
  validate: vEinkommen,
  build(ctx) {
    const s = ctx.store;
    const avgLine = h("p", { class: "field__optional" });
    const updateAvg = () => {
      const zahlungen = gehaltszahlungenProJahr(s.state);
      const netto = parseAmount(s.get("einkommen.nettoMonat"));
      const zusatz = netto * (Math.max(zahlungen - 12, 0) / 12);
      avgLine.textContent = zahlungen > 12 && netto > 0
        ? `Los pagos de salario adicionales equivalen a unos ${formatEUR(zusatz)} al mes (calculado automáticamente).`
        : "";
    };

    const andereWrap = h("div");
    const renderAndere = () => {
      andereWrap.innerHTML = "";
      if (s.get("einkommen.gehaltszahlungen") === "otra")
        andereWrap.appendChild(textInput(s, "einkommen.gehaltszahlungenAndere", { label: "Número de pagos de salario", required: true, type: "number", inputmode: "numeric", onInput: updateAvg }));
      updateAvg();
    };

    const gehaltSelect = selectInput(s, "einkommen.gehaltszahlungen", ["12", "13", "14", "otra"], { label: "Número de pagos de salario al año", required: true, onInput: renderAndere });

    // Comprobante
    const nachweisDetail = h("div");
    const renderNachweis = () => {
      nachweisDetail.innerHTML = "";
      const art = s.get("einkommensnachweis.art");
      if (art === "jahresendabrechnung" || art === "gehaltsabrechnung") {
        nachweisDetail.append(
          note("info", "Sube al menos un archivo: es necesario para el análisis."),
          uploadBlock(s, { kategorie: "Einkommensnachweis", onChange: () => ctx.store.emit() }));
      } else if (art === "keine") {
        nachweisDetail.append(
          textareaInput(s, "einkommensnachweis.begruendung", {
            label: "¿Por qué ahora no dispones de una nómina?", required: true, rows: 3,
            placeholder: "p. ej. autónomo/a, profesional liberal, actualmente sin empleo, sin nómina disponible, otro motivo",
          }),
          note("info", "El cálculo de ingresos y gastos sigue siendo necesario."));
      }
    };

    const artCards = h("div", { class: "options" });
    const nachweisOpts = [
      { value: "jahresendabrechnung", title: "Última liquidación anual del empleador" },
      { value: "gehaltsabrechnung", title: "Nómina actual" },
      { value: "keine", title: "No tengo nómina" },
    ];
    const drawArt = () => {
      artCards.innerHTML = "";
      nachweisOpts.forEach((o) => {
        artCards.appendChild(h("button", { type: "button", class: "opt", attrs: { "aria-pressed": String(s.get("einkommensnachweis.art") === o.value) },
          on: { click: () => { s.set("einkommensnachweis.art", o.value); drawArt(); renderNachweis(); } } },
          h("span", { class: "opt__title", text: o.title })));
      });
    };
    drawArt();
    renderNachweis();
    renderAndere();

    return h("div", {},
      card(
        h("div", { class: "card__title", text: "Tus ingresos" }),
        h("div", { class: "grid-2" },
          moneyInput(s, "einkommen.bruttoMonat", { label: "Ingreso bruto mensual", required: true, hint: "Solo sirve como información y no se suma al importe disponible." }),
          moneyInput(s, "einkommen.nettoMonat", { label: "Ingreso neto mensual", required: true, onInput: updateAvg })),
        gehaltSelect,
        andereWrap,
        avgLine,
        h("div", { class: "grid-3" },
          moneyInput(s, "einkommen.nebeneinkuenfte", { label: "Ingresos adicionales", required: true }),
          moneyInput(s, "einkommen.kindergeld", { label: "Prestación por hijos (Kindergeld)", required: true }),
          moneyInput(s, "einkommen.weitereEinnahmen", { label: "Otros ingresos disponibles", required: true })),
        note("info", "Indica con 0 € los ingresos que no tengas.")
      ),
      card(
        h("div", { class: "card__title", html: "¿Qué comprobante de ingresos quieres subir? <span style='color:var(--neg)'>*</span>" }),
        artCards,
        nachweisDetail
      )
    );
  },
};

/* ---------- 13 · Gastos ---------- */
export const stepAusgaben: Step = {
  id: "ausgaben",
  section: "haushalt",
  eyebrow: "Chequeo del hogar",
  title: "Gastos",
  subtitle: "Completa cada campo: 0 € es posible.",
  validate: vAusgaben,
  build(ctx) {
    const s = ctx.store;

    const postenSumme = summeSparbeitraege(s.state);
    const versSumme = summeVersicherungsbeitraege(s.state);
    // Toma automática: los campos nunca pueden estar por debajo de la suma registrada arriba
    if (postenSumme > 0 && parseAmount(s.get("ausgaben.sparbeitraege")) < postenSumme - 0.005)
      s.set("ausgaben.sparbeitraege", amountToInput(postenSumme));
    if (versSumme > 0 && parseAmount(s.get("ausgaben.versicherungen")) < versSumme - 0.005)
      s.set("ausgaben.versicherungen", amountToInput(versSumme));

    // --- Cuotas de seguros: toma automática (jubilación+capacidad laboral+bienes) + pregunta obligatoria por el excedente ---
    const versSurplusBox = h("div");
    const renderVersSurplus = () => {
      versSurplusBox.innerHTML = "";
      const extra = parseAmount(s.get("ausgaben.versicherungen")) - versSumme;
      if (extra > 0.005) versSurplusBox.append(
        note("info-dark", `Son ${formatEUR(extra)} más que los contratos que registraste arriba (${formatEUR(versSumme)}).`),
        textareaInput(s, "ausgaben.versZielText", { label: "¿Qué seguros adicionales cubre esta cuota más alta?", required: true, rows: 2, placeholder: "p. ej. seguro de salud complementario, seguro de vida de riesgo, otro seguro de bienes …" }),
        h("button", { type: "button", class: "btn-edit", text: "Añadir otro contrato arriba", on: { click: () => ctx.goTo("altersvorsorge") } }),
      );
    };
    const versHint = "Cuotas mensuales de tus seguros. Si un seguro se paga de forma anual o trimestral, conviértelo a mensual (p. ej. cuota anual ÷ 12) y súmalo.";
    const versUebernahme = versSumme > 0
      ? h("p", { class: "field__optional", text: `De los contratos que registraste arriba (jubilación, capacidad laboral, seguros de bienes) ya tomamos ${formatEUR(versSumme)}. Si en total pagas más, indica el importe total.` })
      : null;

    // --- Aportaciones de ahorro actuales: toma automática (bienes) + pregunta obligatoria por el excedente ---
    const sparSurplusBox = h("div");
    const renderSparSurplus = () => {
      sparSurplusBox.innerHTML = "";
      const extra = parseAmount(s.get("ausgaben.sparbeitraege")) - postenSumme;
      if (extra > 0.005) sparSurplusBox.append(
        note("info-dark", `Ahorras ${formatEUR(extra)} más de lo que consta en tus productos de ahorro registrados arriba (${formatEUR(postenSumme)}).`),
        textareaInput(s, "ausgaben.sparZielText", { label: "¿A dónde va este importe adicional?", required: true, rows: 2, placeholder: "p. ej. otro plan de ahorro, reserva en efectivo, cuenta de ahorro a la vista …" }),
        h("button", { type: "button", class: "btn-edit", text: "Añadir otro ahorro arriba", on: { click: () => ctx.goTo("vermoegen") } }),
      );
    };
    const sparHint = postenSumme > 0
      ? `De tus productos de ahorro registrados arriba ya tomamos ${formatEUR(postenSumme)}. Si en total ahorras más, indica aquí el importe total.`
      : "Lo que ya apartas cada mes: se muestra por separado.";

    const node = h("div", {},
      card(
        h("div", { class: "card__title", text: "Gastos corrientes (mensuales)" }),
        h("div", { class: "grid-2" },
          moneyInput(s, "ausgaben.wohnen", { label: "Alquiler con gastos / gastos de vivienda", required: true, hint: "Todos los costos de tu vivienda, incluidos gastos como calefacción, agua y electricidad." }),
          moneyInput(s, "ausgaben.lebenshaltung", { label: "Gastos de vida diaria", required: true, hint: "Alimentos y bebidas: todo para tu día a día." })),
        h("div", { class: "grid-2" },
          moneyInput(s, "ausgaben.mobilitaet", { label: "Movilidad", required: true, hint: "Gasolina, transporte público (p. ej. abono/billete), gastos de auto y vehículo." }),
          moneyInput(s, "ausgaben.kreditraten", { label: "Cuotas de crédito (total)", required: true, hint: "Solo la cuota mensual real, sin amortización aparte." })),
        h("div", { class: "grid-2" },
          moneyInput(s, "ausgaben.unterhalt", { label: "Pensión alimenticia", required: true, hint: "Pagos de manutención, p. ej. para hijos o ex pareja." }),
          moneyInput(s, "ausgaben.abosVereine", { label: "Suscripciones y asociaciones", required: true, hint: "Streaming, móvil, gimnasio, cuotas de socio, etc." })),
        moneyInput(s, "ausgaben.sonstige", { label: "Otros gastos", required: true, hint: "Gastos de ocio y diversión: cine, salir a comer, quedar con amigos, fiestas, etc." }),
        moneyInput(s, "ausgaben.versicherungen", { label: "Cuotas de seguros", required: true, hint: versHint, onInput: renderVersSurplus }),
        versUebernahme,
        versSurplusBox
      ),
      card(
        h("div", { class: "card__title", text: "Aportaciones de ahorro actuales" }),
        h("p", { class: "card__hint", text: sparHint }),
        moneyInput(s, "ausgaben.sparbeitraege", { label: "Aportaciones de ahorro mensuales (total)", required: true, onInput: renderSparSurplus }),
        sparSurplusBox
      )
    );
    renderVersSurplus();
    renderSparSurplus();
    return node;
  },
};

/* ---------- 14 · Resumen del hogar ---------- */
export const stepHaushalt: Step = {
  id: "haushalt",
  section: "haushalt",
  eyebrow: "Chequeo del hogar",
  title: "Tu resumen del hogar",
  subtitle: "Calculado en vivo a partir de tus datos.",
  build(ctx) {
    const s = ctx.store;
    const h2 = berechneHaushalt(s.state);
    const neg = h2.freiVerfuegbar < 0;

    const rc = (label: string, value: number, variant?: "accent" | "pos" | "neg") =>
      h("div", { class: `result-card${variant === "accent" ? " result-card--accent" : ""}` },
        h("div", { class: "result-card__label", text: label }),
        h("div", { class: `result-card__value${variant === "pos" ? " value--pos" : variant === "neg" ? " value--neg" : ""}`, text: formatEUR(value) }));

    return h("div", {},
      card(
        h("div", { class: "result-grid" },
          rc("Ingresos totales", h2.einnahmenGesamt),
          rc("Obligaciones corrientes", h2.laufendeVerbindlichkeiten),
          rc(neg ? "Déficit mensual" : "Importe disponible", h2.freiVerfuegbar, neg ? "neg" : "pos")),
        h("p", { class: "field__optional", style: { marginTop: "12px", marginBottom: "0" }, text: "Obligaciones corrientes = tus gastos, incluidas las aportaciones de ahorro actuales." })
      ),
      neg
        ? note("warn-dark", "Ahora mismo tus gastos superan tus ingresos. No es un problema: vemos juntos dónde hay margen.")
        : note("info-dark", "Se ve sólido. Tomamos este importe disponible como punto de partida para tu planificación de futuro.")
    );
  },
};
